import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiResponse } from "../../../types/APIResponse";
import api from "../../../utils/constants/api";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { EmailTemplate, DeleteEmailTemplate, PostEmailTemplate } from "../../../types/EmailTemplateTypes";
import { toast } from "react-toastify";

// Async Thunks
export const fetchEmailTemplates = createAsyncThunk<EmailTemplate[], { status?: number | string | null }, { rejectValue: string }>("emailTemplates/fetchEmailTemplates", async ({ status }, { rejectWithValue }) => {
  try {
    const statusParam = status ? `?status=${status}` : "";
    const response = await api.get<ApiResponse<EmailTemplate[]>>(`${apiEndpoints.emailTemplate.getAll}${statusParam}`);
    if (response?.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to fetch email templates.");
    }
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const postEmailTemplates = createAsyncThunk<PostEmailTemplate, PostEmailTemplate, { rejectValue: string }>("emailTemplates/postEmailTemplates", async (postEmailTemplateData, { rejectWithValue }) => {
  try {
    const response = await api.post<ApiResponse<any>>(apiEndpoints.emailTemplate.postMailToLostItemUser, { data: postEmailTemplateData });
    if (response?.success) {
      toast.success(response?.message);
      return response.data;
    } else {
      throw new Error(response.message || "Failed to post email.");
    }
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

export const createEmailTemplate = createAsyncThunk<EmailTemplate, EmailTemplate, { rejectValue: string }>("emailTemplates/createEmailTemplate", async (newEmailTemplateData, { rejectWithValue }) => {
  try {
    const response = await api.post<ApiResponse<EmailTemplate>>(apiEndpoints.emailTemplate.create, { data: newEmailTemplateData });
    if (response?.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to add email template.");
    }
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const deleteEmailTemplate = createAsyncThunk<DeleteEmailTemplate, { id: string }, { rejectValue: string }>("emailTemplates/deleteEmailTemplate", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await api.delete<ApiResponse<DeleteEmailTemplate>>(`${apiEndpoints.emailTemplate.delete}/${id}`);
    if (response?.success) {
      return { ...response.data, id };
    } else {
      throw new Error(response.message || "Failed to delete email template.");
    }
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const updateEmailTemplate = createAsyncThunk<EmailTemplate, EmailTemplate, { rejectValue: string }>("emailTemplates/updateEmailTemplate", async (updatedEmailTemplateData, { rejectWithValue }) => {
  try {
    const response = await api.put<ApiResponse<EmailTemplate>>(`${apiEndpoints.emailTemplate.update}/${updatedEmailTemplateData?.id}`, { data: updatedEmailTemplateData });
    if (response?.success) {
      return { ...response.data };
    } else {
      throw new Error(response.message || "Failed to update email template.");
    }
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});


// Update emailTemplates order
export const updateEmailTemplateOrder = createAsyncThunk<EmailTemplate[], { orderUpdates: Array<{ id: string; index: number }> }, { rejectValue: string }>(
  "emailTemplates/updateEmailTemplateOrder",
  async ({ orderUpdates }, { rejectWithValue }) => {
    try {
      const response = await api.put<ApiResponse<EmailTemplate[]>>(apiEndpoints.emailTemplate.updateOrder, { data: { orderUpdates } });
      if (response?.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to update email template order");
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update email template order");
    }
  }
);

export interface NotPaidClaim {
  id: string;
  trackingId: string;
  createdAt: string;
  firstName: string;
  lastName: string;
  email: string;
  lastEmailedAt: string | null;
}

export const fetchNotPaidClaims = createAsyncThunk<NotPaidClaim[], void, { rejectValue: string }>(
  "emailTemplates/fetchNotPaidClaims",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<NotPaidClaim[]>>(apiEndpoints.emailTemplate.getNotPaidClaims);
      if (response?.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch not-paid claims.");
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const sendBulkCustomEmail = createAsyncThunk<any, { templateId: string; lostIds: string[] }, { rejectValue: string }>(
  "emailTemplates/sendBulkCustomEmail",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<any>>(apiEndpoints.emailTemplate.sendBulkCustomEmail, { data });
      if (response?.success) {
        toast.success(response?.message);
        return response.data;
      } else {
        throw new Error(response.message || "Failed to send bulk email.");
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);